import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useModuleAccess from '../../../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../../../core/access/module-access';
import ModuleContext from '../../../_contexts/ModuleContext';
import useOtherApps from './hooks/useOtherApps';

import Button, { ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import MaterialSiteIcon from '@pelckmans/elementis/icons/material-site';

export default function AppQuickActionButton({ size = 'md', emphasized }: Pick<ButtonProps, 'size' | 'emphasized'>) {
  const { t } = useTranslation();
  const { module } = useContext(ModuleContext);
  const { appsAccess } = useModuleAccess(module);
  const appsAllowed = appsAccess === FEATURE_ACCESS.ALLOWED;
  const { otherApps: appsAsButton, getAppActionProps } = useOtherApps();

  if (appsAsButton.length === 0) return null;

  if (appsAsButton.length === 1) {
    const [singleApp] = appsAsButton;

    return (
      <Button
        key="app-quick-action-button"
        startIcon={<MaterialSiteIcon data-testid="app-quick-action-icon" />}
        prominence="secondary"
        size={size}
        disabled={!appsAllowed}
        tooltip={!appsAllowed && t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.NO_LICENSE_TOOLTIP')}
        emphasized={emphasized}
        {...getAppActionProps(singleApp)}
      >
        {t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.OPEN_QUICK_ACTION', { appName: singleApp.name })}
      </Button>
    );
  }

  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.OPEN_QUICK_ACTION_MULTIPLE')}
      key="app-quick-action-button"
      buttonProps={{
        prominence: 'secondary',
        startIcon: <MaterialSiteIcon data-testid="app-quick-action-icon" />,
        disabled: !appsAllowed,
      }}
      size={size}
      tooltip={!appsAllowed && t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.NO_LICENSE_TOOLTIP')}
      disableBackgroundScroll
      emphasized={emphasized}
    >
      {appsAsButton.map(appAsButton => {
        const isPreview = appAsButton.state === 'preview';
        const disabledProps = isPreview
          ? ({ disabled: true, tooltip: t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.PREVIEW_TOOLTIP') } as const)
          : {};
        const subText = appAsButton.linkedApp?.manualIncluded
          ? t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.MANUAL_INCLUDED')
          : undefined;

        return (
          <ButtonDropdown.Item
            key={appAsButton.id}
            value={appAsButton.linkedApp?.id}
            text={appAsButton.name}
            subText={subText}
            textIcon={<MaterialSiteIcon data-testid={`app-quick-action-icon-${appAsButton.id}`} />}
            {...disabledProps}
            {...getAppActionProps(appAsButton)}
          />
        );
      })}
    </ButtonDropdown>
  );
}
